import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversations } from '../hooks';
import { useAuthStore } from '@/features/auth';
import { getConversationDisplayName, getConversationAvatar } from '../hooks';
import { ChatAvatar } from './ChatAvatar';

interface ConversationPickerProps {
  selectedIds: string[];
  onToggle: (conversationId: string) => void;
  excludeIds?: string[];
  maxSelections?: number;
}

export function ConversationPicker({
  selectedIds,
  onToggle,
  excludeIds = [],
  maxSelections,
}: ConversationPickerProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const { data: conversations, isLoading } = useConversations();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredConversations = useMemo(() => {
    if (!conversations) return [];

    return conversations
      .filter((conv) => !excludeIds.includes(conv.id))
      .filter((conv) => {
        if (!searchQuery.trim()) return true;
        const displayName = getConversationDisplayName(conv, currentUserId || '');
        return displayName.toLowerCase().includes(searchQuery.toLowerCase());
      })
      .sort((a, b) => {
        // Selected conversations first
        const aSelected = selectedIds.includes(a.id);
        const bSelected = selectedIds.includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // Then by last message timestamp
        const aTime = a.lastMessage?.createdAt || a.createdAt;
        const bTime = b.lastMessage?.createdAt || b.createdAt;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });
  }, [conversations, excludeIds, searchQuery, selectedIds, currentUserId]);

  const handleToggle = (conversationId: string) => {
    const isSelected = selectedIds.includes(conversationId);
    const canSelect = !maxSelections || selectedIds.length < maxSelections;

    if (isSelected || canSelect) {
      onToggle(conversationId);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'channel':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M7 10h10M7 14h10" strokeWidth="2" strokeLinecap="round" />
            <rect x="4" y="6" width="16" height="12" rx="2" strokeWidth="2" />
          </svg>
        );
      case 'group':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeWidth="2" />
            <circle cx="9" cy="7" r="4" strokeWidth="2" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getParticipantCount = (conversation: any) => {
    if (conversation.type === 'dm') return null;
    const count = conversation.participants?.length || 0;
    return count > 0 ? count : null;
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="border border-white/20 bg-white/80 p-3 rounded-xl animate-pulse"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-100 rounded w-1/3" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-800/40">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" strokeWidth="2" />
            <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('messaging.forward.searchConversations')}
          className="w-full pl-10 pr-4 py-2 rounded-xl border border-white/20 bg-white/80 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-slate-400 font-medium"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-800/40 hover:text-neutral-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Conversation List */}
      <div className="border border-white/20 bg-white/80 max-h-[300px] overflow-y-auto rounded-xl glass-surface">
        {filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-neutral-800/60">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <path d="M8 15h8M9 9h.01M15 9h.01" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <p className="font-medium">
              {searchQuery
                ? t('messaging.forward.noConversationsFound')
                : t('messaging.forward.noConversations')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-50">
            {filteredConversations.map((conversation) => {
              const isSelected = selectedIds.includes(conversation.id);
              const displayName = getConversationDisplayName(conversation, currentUserId || '');
              const avatar = getConversationAvatar(conversation, currentUserId || '');
              const participantCount = getParticipantCount(conversation);
              const isDisabled = !isSelected && maxSelections && selectedIds.length >= maxSelections;

              return (
                <button
                  key={conversation.id}
                  onClick={() => handleToggle(conversation.id)}
                  disabled={isDisabled}
                  className={`w-full p-3 flex items-center gap-3 hover:bg-neutral-50 transition-colors text-left ${
                    isSelected ? 'bg-slate-100' : ''
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <ChatAvatar
                      src={avatar}
                      name={displayName}
                      fallbackClass="bg-slate-700 text-white"
                    />
                    {conversation.type !== 'dm' && (
                      <div className="absolute -bottom-1 -right-1 bg-slate-800 text-white rounded-full p-0.5">
                        {getTypeIcon(conversation.type)}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-neutral-800 truncate">{displayName}</h3>
                      {participantCount && (
                        <span className="text-xs text-neutral-500 font-medium flex-shrink-0">
                          {participantCount} {t('messaging.forward.members')}
                        </span>
                      )}
                    </div>
                    {conversation.lastMessage && (
                      <p className="text-sm text-neutral-500 truncate">
                        {conversation.lastMessage.type === 'text'
                          ? conversation.lastMessage.content
                          : t(`messaging.messageTypes.${conversation.lastMessage.type}`)}
                      </p>
                    )}
                  </div>

                  {/* Checkbox */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-5 h-5 border border-white/20 rounded flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-slate-700' : 'bg-white/80'
                      }`}
                    >
                      {isSelected && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <polyline points="20 6 9 17 4 12" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Max Selection Warning */}
      {maxSelections && selectedIds.length >= maxSelections && (
        <div className="border border-white/20 bg-slate-100 p-3 rounded-xl flex items-start gap-2">
          <svg className="w-5 h-5 text-neutral-800 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
            <path d="M12 8v4M12 16h.01" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <p className="text-sm font-medium text-neutral-800">
            {t('messaging.forward.maxSelectionsReached', { max: maxSelections })}
          </p>
        </div>
      )}
    </div>
  );
}
